/**
 * South Florida Pet Dental Hub Page
 * Central location page with links to all city-specific pages
 */

import React from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs, { createBreadcrumbs } from '../components/Breadcrumbs';
import BookAppointmentLink from '../components/BookAppointmentLink';
import { RelatedPosts, ContextualCTA } from '../components/InternalLinks';
import Footer from '../components/Footer';
import { locations, services } from '../data/seo';

const LocationSEO: React.FC = () => {
  const breadcrumbs = createBreadcrumbs.page('South Florida Pet Dental', '/south-florida-pet-dental');

  // Group locations by county
  const locationsByCounty = locations.reduce((acc, loc) => {
    const county = loc.county;
    if (!acc[county]) acc[county] = [];
    acc[county].push(loc);
    return acc;
  }, {} as Record<string, typeof locations>);

  const petDentalKeywords = [
    "dog dentist", "cat dentist", "pet dental care", "dog teeth cleaning", 
    "cat teeth cleaning", "veterinary dentist", "pet dentist near me", 
    "dog dental cleaning", "cat dental cleaning", "professional pet dental", 
    "veterinary dental specialist", "pet oral health", "animal dentist",
    "pet dental surgery", "pet tooth extraction", "veterinary dental x-rays"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-teal to-forest-green text-white py-20">
        <div className="container-custom">
          <Breadcrumbs items={breadcrumbs} className="mb-6 text-white/70" />
          
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-nantes">
              South Florida's Premier Pet Dental Specialists
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Professional dog and cat dental care serving Fort Lauderdale, Miami, Boca Raton, and {locations.length - 3} more cities across South Florida.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <BookAppointmentLink 
                className="bg-white text-brand-teal px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg"
              >
                <i className="fas fa-calendar-alt mr-2"></i>
                Book Now
              </BookAppointmentLink>
              <a 
                href="tel:+19544663775" 
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-brand-teal transition-all duration-300"
              >
                <i className="fas fa-phone mr-2"></i>
                (954) 466-3775
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-12 border-b">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold text-brand-teal mb-2">{locations.length}+</div>
              <div className="text-gray-600">Cities Served</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-brand-teal mb-2">3</div>
              <div className="text-gray-600">Counties</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-brand-teal mb-2">5.0</div>
              <div className="text-gray-600">Star Rating</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-brand-teal mb-2">100%</div>
              <div className="text-gray-600">Dental Focused</div>
            </div>
          </div>
        </div>
      </section>

      {/* Locations by County */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 font-nantes">
              Find Pet Dental Care Near You
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Click on your city to learn more about our services in your area and get directions to our Fort Lauderdale clinic.
            </p>
          </div>

          <div className="space-y-12">
            {Object.entries(locationsByCounty).map(([county, locs]) => (
              <div key={county}>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center gap-3">
                  <i className="fas fa-map-marker-alt text-brand-teal"></i>
                  {county} County
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {locs.map((location) => (
                    <Link
                      key={location.slug}
                      to={`/locations/${location.slug}`}
                      className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 group border border-gray-100"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-brand-teal/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-teal/20 transition-colors">
                          <i className="fas fa-city text-brand-teal text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-800 group-hover:text-brand-teal transition-colors mb-1">
                            {location.name}
                          </h4>
                          <p className="text-sm text-brand-teal font-medium mb-2">
                            {location.driveTime !== '0 minutes' ? `${location.driveTime} from clinic` : 'Clinic Location'}
                          </p>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            Serving {location.neighborhoods.slice(0, 3).join(', ')} and more
                          </p>
                          <div className="mt-3 flex flex-wrap gap-1">
                            {location.zipCodes.slice(0, 3).map((zip) => (
                              <span key={zip} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">
                                {zip}
                              </span>
                            ))}
                            {location.zipCodes.length > 3 && (
                              <span className="text-gray-400 text-xs px-1">
                                +{location.zipCodes.length - 3}
                              </span>
                            )}
                          </div>
                        </div>
                        <i className="fas fa-chevron-right text-gray-400 group-hover:text-brand-teal transition-colors"></i>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 font-nantes">
              Our Pet Dental Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive veterinary dental care at transparent, flat-rate prices.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {services.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all group border border-gray-100"
              >
                <div className="w-16 h-16 bg-brand-teal/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-brand-teal/20 transition-colors">
                  <i className="fas fa-tooth text-brand-teal text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 group-hover:text-brand-teal transition-colors mb-2">
                  {service.name}
                </h3>
                <p className="text-gray-600 mb-4">{service.shortDescription}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-brand-teal">{service.price}</span>
                  <span className="text-brand-teal font-medium group-hover:underline">
                    Learn More →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Keywords/Services Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 font-nantes">
              Comprehensive Veterinary Dental Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We specialize in all aspects of pet oral health throughout South Florida.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {petDentalKeywords.map((keyword, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <i className="fas fa-check-circle text-brand-teal text-lg mb-2"></i>
                <p className="font-medium text-gray-800 capitalize">{keyword}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Section */}
      <section className="section-padding bg-brand-teal text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-nantes">
            Pet Dental Care When You Need It
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Whether your pet needs routine dental cleaning or urgent care for a dental problem, 
            CHOMP Pet Dental is here for pet families across South Florida.
          </p>
          <div className="grid md:grid-cols-4 gap-6 mb-10">
            <div className="bg-white/10 rounded-xl p-6">
              <i className="fas fa-tooth text-4xl mb-4"></i>
              <h3 className="font-bold text-lg mb-2">Dental Cleanings</h3>
              <p className="text-sm opacity-90">Professional cleaning under anesthesia</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <i className="fas fa-x-ray text-4xl mb-4"></i>
              <h3 className="font-bold text-lg mb-2">Digital X-Rays</h3>
              <p className="text-sm opacity-90">Full mouth imaging included</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <i className="fas fa-procedures text-4xl mb-4"></i>
              <h3 className="font-bold text-lg mb-2">Oral Surgery</h3>
              <p className="text-sm opacity-90">Extractions & advanced procedures</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <i className="fas fa-heartbeat text-4xl mb-4"></i>
              <h3 className="font-bold text-lg mb-2">Pain Management</h3>
              <p className="text-sm opacity-90">Compassionate pain control</p>
            </div>
          </div>
          <a 
            href="tel:+19544663775" 
            className="inline-flex items-center bg-white text-brand-teal px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-lg"
          >
            <i className="fas fa-phone mr-2"></i>
            Call (954) 466-3775
          </a>
        </div>
      </section>

      {/* Blog Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-primary mb-8 text-center font-nantes">
              Pet Dental Health Resources
            </h2>
            <RelatedPosts limit={3} title="" />
            <div className="text-center mt-8">
              <Link to="/blog" className="text-brand-teal font-semibold hover:underline text-lg">
                Read More Pet Dental Tips →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 font-nantes">
            Ready to Give Your Pet the Best Dental Care?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join pet families across South Florida who trust CHOMP Pet Dental for their pets' oral health.
          </p>
          <ContextualCTA variant="box" />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationSEO;
